import type { CardDef } from '@/upgrades/cards';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import type { CrateDestination, HangarAction } from '@/ui/shop-types';
import { pickupNotice, pickupNoticeColor, scrapGems } from '@/ui/weapon-labels';
import { sfx } from '@/audio/sfx';
import type { RunState } from './run-state';
import {
  bagCap, bagCount, bagPrice, cardContext, crateDestination, hangarCard, socketPrice,
} from './ship';
import type { ShopFlow } from './shop-flow';

/**
 * Sockets + bag (docs/09 wave 4). Pickups NEVER swap an equipped weapon:
 * an empty socket fills, else the bag takes it, else it waits as the
 * swap-or-scrap crate. At the break one action equips a bag weapon into a
 * socket (an occupied socket swaps back into the bag), scraps pay gems,
 * padlocked sockets / bag slots are bought in place.
 */
export class Hangar {
  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly shop: ShopFlow,
    private readonly hud: Hud,
    private readonly save: () => void,
  ) {
    // The crate label previews where a pickup would go
    world.drops.destinationOf = (id) => this.destination(id);
    world.drops.nextSocket = () => world.player.weapon.equipped.length;
  }

  destination(weaponId: string): CrateDestination {
    return crateDestination(this.world.player, this.run, weaponId);
  }

  act(a: HangarAction): void {
    switch (a.kind) {
      case 'equip': return this.equip(a.weaponId, a.socket);
      case 'scrap': return this.scrap(a.weaponId);
      case 'buySocket': return this.buySocket();
      case 'buyBag': return this.buyBag();
    }
  }

  /** Crate pickup mid-fight — never touches an occupied socket. */
  addWeapon(weaponId: string): void {
    const { run, world } = this;
    const dest = this.destination(weaponId);
    if (dest === 'scrap') {
      run.gems += scrapGems(weaponId);
    } else {
      run.freshWeapons.push(weaponId);
      if (dest === 'full') run.pendingWeapon = weaponId;
      else run.inventory.push(weaponId);
      if (dest === 'socket') {
        world.player.setEquipped([...world.player.weapon.equipped, weaponId], run.inventory);
      }
    }
    const socket = world.player.weapon.equipped.indexOf(weaponId);
    this.hud.weaponPickup(weaponId, dest, socket);
    this.hud.notify(pickupNotice(weaponId, dest, socket), 'pickup', pickupNoticeColor(weaponId));
  }

  /** CONTINUE: an unresolved swap-or-scrap crate is scrapped for gems. */
  settle(): void {
    const run = this.run;
    if (run.pendingWeapon) run.gems += scrapGems(run.pendingWeapon);
    run.pendingWeapon = null;
    run.freshWeapons = [];
  }

  /** Bag weapon (or the waiting crate) → socket; an occupied socket swaps. */
  private equip(weaponId: string, socket: number): void {
    const { run, world } = this;
    const holder = world.player.weapon;
    const eq = [...holder.equipped];
    const fromCrate = run.pendingWeapon === weaponId;
    const inBag = run.inventory.includes(weaponId) && !eq.includes(weaponId);
    if ((!fromCrate && !inBag) || socket < 0 || socket >= holder.capacity) {
      sfx.uiDeny();
      return;
    }
    const old = socket < eq.length ? eq[socket]! : null;
    if (old) eq[socket] = weaponId;
    else eq.push(weaponId);
    if (fromCrate) {
      // The displaced socket weapon becomes the waiting crate
      if (old) run.inventory[run.inventory.indexOf(old)] = weaponId;
      else run.inventory.push(weaponId);
      run.pendingWeapon = old;
    } else if (old) {
      // The swapped-out weapon takes the bag place of the one equipped
      const i = run.inventory.indexOf(weaponId);
      run.inventory[run.inventory.indexOf(old)] = weaponId;
      run.inventory[i] = old;
    }
    run.freshWeapons = run.freshWeapons.filter((id) => id !== weaponId);
    world.player.setEquipped(eq, run.inventory);
    sfx.uiConfirm();
    this.commit();
  }

  /** Scrap a BAG weapon or the waiting crate for yellow (sockets stay armed). */
  private scrap(weaponId: string): void {
    const { run, world } = this;
    if (run.pendingWeapon === weaponId) {
      run.pendingWeapon = null;
    } else if (run.inventory.includes(weaponId) && !world.player.weapon.equipped.includes(weaponId)) {
      run.inventory = run.inventory.filter((id) => id !== weaponId);
    } else {
      sfx.uiDeny();
      return;
    }
    const value = scrapGems(weaponId);
    run.gems += value;
    run.freshWeapons = run.freshWeapons.filter((id) => id !== weaponId);
    this.absorbCrate();
    sfx.uiConfirm();
    this.shop.flashGain('gems', value);
    this.commit();
  }

  /** Padlocked socket: pays red and applies the Weapon Rack card. */
  private buySocket(): void {
    const price = socketPrice(this.world.player);
    if (price === null || this.run.red < price) {
      sfx.uiDeny();
      return;
    }
    this.run.red -= price;
    this.applyCard(hangarCard('rack'));
  }

  /** Padlocked bag slot: pays yellow and applies the Cargo Bay card. */
  private buyBag(): void {
    const price = bagPrice(this.world.player, this.run);
    if (price === null || this.run.gems < price) {
      sfx.uiDeny();
      return;
    }
    this.run.gems -= price;
    this.applyCard(hangarCard('cargo'));
    this.absorbCrate();
  }

  private applyCard(c: CardDef): void {
    c.apply(cardContext(this.world.player, this.run));
    this.run.ownedCards.push(c);
    sfx.uiConfirm();
    this.commit();
  }

  /** Room in the bag again → the waiting crate drops in. */
  private absorbCrate(): void {
    const { run, world } = this;
    if (run.pendingWeapon && bagCount(world.player, run) < bagCap(world.player)) {
      run.inventory.push(run.pendingWeapon);
      run.pendingWeapon = null;
    }
  }

  private commit(): void {
    this.shop.refresh();
    this.save();
  }
}
